import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  CheckCircle2, Phone, ChevronRight, ShieldCheck, Clock, Wrench,
  Layers, AlertTriangle, RefreshCw, Calculator as CalcIcon
} from "lucide-react";
import { getService, SERVICES } from "../data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tank-replace-pro/artifacts/isbxf87e_Logo%20Kool%20Plumbing-01.png";
const SITE_URL = "https://koolplumbing.com";

const ICONS = { layers: Layers, wrench: Wrench, shield: ShieldCheck, alert: AlertTriangle };
const SERVICE_TYPE_MAP = {
  "septic-tank-replacement": "septic_replacement",
  "drain-field-repair": "septic_repair",
  "septic-pumping": "septic_pumping",
  "emergency-plumbing": "general_plumbing",
};

const ServicePage = ({ addToast }) => {
  const { slug } = useParams();
  const svc = getService(slug);

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", zipCode: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!svc) return <Navigate to="/" replace />;

  const Icon = ICONS[svc.icon] || Wrench;
  const canonical = `${SITE_URL}/services/${svc.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Service", serviceType: svc.name, provider: { "@type": "Plumber", name: "Kool Plumbing", telephone: "+1-786-622-4963", url: SITE_URL }, areaServed: [{ "@type": "AdministrativeArea", name: "Miami-Dade County" }, { "@type": "AdministrativeArea", name: "Broward County" }], name: svc.name, description: svc.metaDesc, url: canonical },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/#services` },
        { "@type": "ListItem", position: 3, name: svc.name, item: canonical },
      ] },
      { "@type": "FAQPage", mainEntity: svc.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(false);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        zip_code: formData.zipCode, service_type: SERVICE_TYPE_MAP[svc.slug] || "general_plumbing",
        message: `[${svc.name} page] ${formData.message}`,
      });
      setSuccess(true);
      addToast && addToast("Request sent! Our specialist will call you shortly.", "success");
      setFormData({ name: "", email: "", phone: "", zipCode: "", message: "" });
    } catch (err) {
      addToast && addToast("Failed to submit request. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 font-body bg-white" data-testid={`service-page-${svc.slug}`}>
      <Helmet>
        <title>{svc.metaTitle}</title>
        <meta name="description" content={svc.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={svc.metaTitle} />
        <meta property="og:description" content={svc.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative py-20 bg-cover bg-center" style={{ backgroundImage: `url('${svc.image}')` }}>
        <div className="absolute inset-0 bg-slate-900/78"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-white space-y-6">
          <nav className="text-xs text-blue-200 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight size={12} />
            <a href="/#services" className="hover:text-amber-400">Services</a>
            <ChevronRight size={12} />
            <span className="text-amber-400">{svc.short}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-600/30 border border-amber-600/50 text-amber-300 text-sm font-semibold">
            <Icon size={16} /> {svc.flagship ? "Our Signature Specialty" : "Professional Service"}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading leading-tight tracking-tight max-w-3xl">{svc.name}</h1>
          <p className="text-lg text-gray-200 max-w-2xl leading-relaxed">{svc.tagline}</p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <a href="#quote" data-testid="service-cta-quote" className="px-8 py-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide shadow-lg transition-all transform hover:-translate-y-0.5 text-center">Get a Free Quote</a>
            <a href="tel:+17866224963" className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold backdrop-blur transition-all flex items-center justify-center gap-2"><Phone size={18} /> (786) 622-4963</a>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-blue-950 py-6">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          {[["Licensed", "#CFC1432999"], ["Free Quote", "On-Site Inspections"], ["24/7 Care", "Emergency Service"], ["15+ Years", "Local Experience"]].map(([t, s]) => (
            <div key={t} className="space-y-1"><span className="text-amber-500 font-heading text-lg font-bold block">{t}</span><span className="text-xs text-gray-400">{s}</span></div>
          ))}
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="space-y-4">
              {svc.intro.map((p, i) => (<p key={i} className="text-gray-600 leading-relaxed">{p}</p>))}
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl font-heading text-blue-950">What's included</h2>
              <ul className="space-y-3 text-sm text-gray-600">
                {svc.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3"><span className="text-emerald-500 mt-0.5"><CheckCircle2 size={18} /></span><span>{b}</span></li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 bg-amber-50 border border-amber-100 rounded-2xl p-6">
              <h3 className="text-xl font-heading text-blue-950 flex items-center gap-2"><AlertTriangle size={20} className="text-amber-600" /> {svc.signsTitle}</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {svc.signs.map((s) => (
                  <div key={s} className="flex items-start gap-2 text-sm text-gray-700"><ChevronRight size={15} className="text-amber-600 mt-0.5" /><span>{s}</span></div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">How it works</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {svc.process.map(([t, d], i) => {
                  const Pico = [CalcIcon, ShieldCheck, Wrench, Clock][i % 4];
                  return (
                    <div key={t} className="p-5 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center"><Pico size={20} /></div>
                      <h4 className="font-heading text-blue-950 text-base">{i + 1}. {t}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed">{d}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-heading text-blue-950">Frequently asked questions</h3>
              <div className="space-y-3">
                {svc.faqs.map(([q, a]) => (
                  <details key={q} className="group bg-gray-50 rounded-xl border border-gray-100 p-5">
                    <summary className="font-semibold text-blue-950 text-sm cursor-pointer list-none flex justify-between items-center">{q}<ChevronRight size={16} className="group-open:rotate-90 transition-transform text-amber-600" /></summary>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">{a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR FORM */}
          <div className="lg:col-span-1">
            <div id="quote" className="sticky top-24 bg-white border border-gray-200 rounded-2xl shadow-lg p-6 space-y-4">
              <h3 className="text-xl font-heading text-blue-950">Request a Free Quote</h3>
              <p className="text-xs text-gray-500">{svc.short} in Miami-Dade & Broward. We respond within 2 business hours.</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input data-testid="service-lead-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full Name *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="service-lead-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="service-lead-phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <input data-testid="service-lead-zip" required value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} placeholder="Property Zip Code *" className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <textarea data-testid="service-lead-message" rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Briefly describe your project" className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button data-testid="service-lead-submit" type="submit" disabled={isSubmitting} className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-xl shadow transition disabled:bg-gray-200 flex items-center justify-center gap-2">{isSubmitting ? <RefreshCw className="animate-spin" size={18} /> : "Submit Free Request"}</button>
              </form>
              {success && (<div data-testid="service-lead-success" className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex gap-2 items-center"><CheckCircle2 size={16} /> <span>Success! We'll call you shortly.</span></div>)}
              <Link to="/#calculator" className="block text-center text-xs text-blue-700 hover:underline pt-1">Try our instant cost estimator →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 space-y-6">
          <h3 className="text-xl font-heading text-blue-950">Explore our other services</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {SERVICES.filter((s) => s.slug !== svc.slug).map((s) => {
              const SIcon = ICONS[s.icon] || Wrench;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`} className="group p-5 bg-white border border-gray-200 rounded-2xl hover:border-amber-400 hover:shadow-md transition space-y-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center"><SIcon size={20} /></div>
                  <h4 className="font-heading text-blue-950 text-base flex items-center justify-between">{s.short}<ChevronRight size={16} className="text-gray-300 group-hover:text-amber-600 transition" /></h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.tagline}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="bg-blue-950 text-white py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Kool Plumbing" className="h-12 w-auto" />
          <div className="text-xs text-gray-300 text-center sm:text-right space-y-1">
            <p className="flex items-center gap-2 justify-center sm:justify-end"><Phone size={13} className="text-amber-500" /> (786) 622-4963 · info@koolplumbing.com</p>
            <p>Serving Miami-Dade &amp; Broward · License #CFC1432999</p>
            <Link to="/" className="text-amber-500 hover:underline">← Back to homepage</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
